
	<v-card
		class="system-card"
		:class="{ 'system-card--error': systemStatus === 'runningWithErrors' }"
		elevation="2"
		rounded
		:aria-label="`${systemIntegration.serviceName} system with ${integrationServices.length} services`"
		role="region"
		:data-testid="`system-card-${systemIntegration.id}`"
	>
		<v-card-text class="system-content">
			<!-- System Name Header -->
			<div class="system-header-integrated mb-0">
				<div class="system-header-content">
					<div class="system-info">
						<div class="system-title-row">
							<div class="system-info-left">
								<div class="system-name-row">
									<div>
										<div class="system-name-container">
											<!-- Status Icon (SVG, sharp, white, pulsing for errors) -->
											<h3
												class="system-name"
												:data-testid="`system-name-${systemIntegration.id}`"
											>
												<v-avatar
													v-if="systemIntegration.icon"
													:color="systemIntegration.color || 'white'"
													size="24"
													class="mr-2 system-avatar"
													role="img"
													:aria-label="`System template: ${systemIntegration.serviceName}`"
												>
													<font-awesome-icon
														v-if="systemIntegration.icon && systemIntegration.icon.startsWith('fa')"
														:icon="getFontAwesomeIcon(systemIntegration.icon)"
														:style="{ color: systemIntegration.iconColor || 'white', fontSize: '16px' }"
														aria-hidden="true"
													/>
													<v-icon
														v-else-if="systemIntegration.icon"
														size="16"
														:color="systemIntegration.iconColor || 'white'"
														aria-hidden="true"
													>
														{{ systemIntegration.icon }}
													</v-icon>
												</v-avatar>
												<v-avatar
													v-else
													color="#ed303e"
													size="24"
													class="mr-2 system-avatar"
													role="img"
													:aria-label="`System template: ${systemIntegration.serviceName}`"
												>
													<v-img
														:src="cassieIcon"
														width="14"
														height="14"
														contain
														class="system-icon"
														aria-hidden="true"
													/>
												</v-avatar>
												{{ systemIntegration.serviceName }}
											</h3>
											<v-tooltip bottom>
												<template #activator="{ on, attrs }">
													<div
														class="last-run-indicator"
														v-bind="attrs"
														v-on="on"
													>
														|
														<v-icon
															small
															aria-hidden="true"
															class="ml-1"
															:color="`${systemIntegration.enabled ? '#059669' : ''}`"
														>
															{{ systemIntegration.enabled ? 'mdi-play-circle-outline' : 'mdi-pause-circle-outline' }}
														</v-icon>
														<span
															class="last-run-text"
															:data-testid="`system-frequency-${systemIntegration.id}`"
															:aria-label="`Execution frequency: ${systemIntegration.enabled ? formatFrequency(systemIntegration.frequencyInSeconds) : 'System disabled'}`"
														>
															{{ systemIntegration.enabled ? 'Running every ' + formatFrequency(systemIntegration.frequencyInSeconds) : 'Disabled' }}
														</span>
													</div>
												</template>
												<span>
													{{ systemIntegration.enabled ? 'How often services in this system are executed' : 'System is currently disabled' }}
												</span>
											</v-tooltip>
										</div>

										<div
											v-if="systemIntegration.notes"
											class="system-description"
											:data-testid="`system-description-${systemIntegration.id}`"
										>
											{{ systemIntegration.notes }}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<!-- System Menu -->
					<div class="system-menu-top-right">
						<v-menu
							offset-y
							left
						>
							<template #activator="{ on, attrs }">
								<v-btn
									icon
									x-small
									v-bind="attrs"
									:aria-label="'System actions menu'"
									:data-testid="`system-menu-${systemIntegration.id}`"
									v-on="on"
								>
									<v-icon>
										mdi-dots-horizontal
									</v-icon>
								</v-btn>
							</template>
							<v-list
								dense
								role="menu"
								:aria-label="`Actions for ${systemIntegration.serviceName} system`"
							>
								<v-list-item
									v-if="systemIntegration.enabled && userHasFullConnectorServicePermission"
									role="menuitem"
									:data-testid="`disable-system-${systemIntegration.id}`"
									@click="handleSystemToggle"
								>
									<v-list-item-title>
										Disable System
									</v-list-item-title>
								</v-list-item>
								<v-list-item
									v-else-if="userHasFullConnectorServicePermission"
									role="menuitem"
									:data-testid="`enable-system-${systemIntegration.id}`"
									@click="handleSystemToggle"
								>
									<v-list-item-title>
										Enable System
									</v-list-item-title>
								</v-list-item>

								<v-list-item
									v-if="userHasFullConnectorServicePermission && systemIntegration.isEditable"
									role="menuitem"
									:data-testid="`edit-system-${systemIntegration.id}`"
									@click="handleSystemEdit"
								>
									<v-list-item-title>
										Edit
									</v-list-item-title>
								</v-list-item>

								<v-list-item
									v-else
									role="menuitem"
									:data-testid="`view-system-${systemIntegration.id}`"
									@click="handleSystemEdit"
								>
									<v-list-item-title>
										View
									</v-list-item-title>
								</v-list-item>

								<v-list-item
									v-if="userHasFullConnectorServicePermission"
									role="menuitem"
									:data-testid="`export-system-${systemIntegration.id}`"
									@click="handleSystemExport"
								>
									<v-list-item-title>
										Export
									</v-list-item-title>
								</v-list-item>

								<v-list-item
									v-if="userHasFullConnectorServicePermission"
									role="menuitem"
									:data-testid="`clone-system-${systemIntegration.id}`"
									@click="handleSystemClone"
								>
									<v-list-item-title>
										Clone
									</v-list-item-title>
								</v-list-item>

								<v-list-item
									v-if="userHasFullConnectorServicePermission"
									role="menuitem"
									:data-testid="`add-service-${systemIntegration.id}`"
									@click="handleCreateService"
								>
									<v-list-item-title>
										Add Integration
									</v-list-item-title>
								</v-list-item>

								<v-list-item
									v-if="userHasFullConnectorServicePermission"
									:disabled="!systemIntegration.isEditable"
									role="menuitem"
									:data-testid="`delete-system-${systemIntegration.id}`"
									@click.capture.stop.prevent="systemIntegration.isEditable ? handleDeleteSystem() : null"
								>
									<v-list-item-title>
										Delete
									</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</div>
				</div>
			</div>

			<!-- Integration Service Cards List -->
			<div
				class="integration-cards-container"
				role="list"
				:aria-label="`${localServices.length} services in ${systemIntegration.serviceName} system`"
				:data-testid="`services-list-${systemIntegration.id}`"
			>
				<div
					v-if="integrationServices.length === 0"
					class="no-integrations-state"
					style="cursor: pointer"
					@click="handleCreateService"
				>
					<div class="create-integration-content">
						<div class="create-icon-container">
							<v-icon
								color="primary"
								size="32"
								aria-hidden="true"
							>
								mdi-plus-circle
							</v-icon>
						</div>
						<div class="create-content">
							<h4 class="create-title">
								Create New Integration
							</h4>
							<p class="create-subtitle">
								Integrate with endpoints to automatically sync data between Cassie and your target system.
							</p>
						</div>
						<div class="create-action">
							<v-btn
								color="primary"
								outlined
								:aria-label="`Create new integration in ${systemIntegration.serviceName} system`"
								:data-testid="`create-service-${systemIntegration.id}`"
								class="secondary-action-button text-none"
							>
								Get Started
							</v-btn>
						</div>
					</div>
				</div>
				<div v-else>
					<draggable
						v-model="localServices"
						:group="`system-${systemIntegration.id}-services`"
						:disabled="!systemIntegration.enabled"
						ghost-class="integration-ghost"
						chosen-class="integration-chosen"
						drag-class="integration-drag"
						:force-fallback="true"
						:fallback-tolerance="5"
						handle=".drag-handle"
						:animation="200"
role="list" :aria-label="`Reorderable list of ${localServices.length} services in ${systemIntegration.serviceName} system`" :data-testid="`draggable-services-${systemIntegration.id}`" @end="handleDragEnd"
					>
						<div
							v-for="(service, index) in localServices"
							:key="service.id"
							class="integration-item"
							:class="{ 'mb-4': index < localServices.length - 1 }"
							role="listitem"
						>
							<IntegrationCard
								:integration-service="getServiceWithSystemState(service)"
								:is-draggable="true"
								:show-endpoint="showEndpoint()"
								:user-has-log-view-permission="userHasLogViewPermission"
								:user-has-full-connector-service-permission="userHasFullConnectorServicePermission"
								@change:active="$emit('toggle-service-enabled', $event)"
								@edit-service="handleEditService"
								@export-service="$emit('export-service', $event)"
								@clone-service="handleCloneService"
								@view-failures="$emit('view-failures', $event)"
								@delete-service="$emit('delete-service', $event)"
							/>
						</div>
					</draggable>
				</div>
			</div>
		</v-card-text>
	</v-card>
